import { Suspense, useEffect } from "react";
import Nav from "./ui/Nav";
import Home from "./ui/Home";
import Footer from "./ui/Footer";
import SharePage from "./ui/SharePage";
import Celebration from "./ui/Celebration";
import GeoSparkles from "./ui/GeoSparkles";
import MissingHall from "./ui/MissingHall";
import ErrorBoundary from "./ui/ErrorBoundary";
import { startPopLink } from "./net/popSocket";
import { initSmoothScroll } from "./lib/smoothScroll";
import { usePath } from "./lib/router";
import { findExperience } from "./experiences/registry";
import { useSettingsStore } from "./state/useSettingsStore";

/** /s/:n — a shared pop. Parameterised, so it sits outside the hall registry. */
function shareNumber(path: string): number | null {
  const m = path.match(/^\/s\/(\d{1,13})$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n >= 1 && n <= 1e12 ? n : null;
}

export default function App() {
  const reducedMotion = useSettingsStore((s) => s.reducedMotion);

  useEffect(() => {
    // Connect site-wide, not just in the halls that show the tally: a milestone
    // is meant to reach whoever is here, wherever they are standing. Idle
    // hibernated sockets cost nothing, and keepalives are answered at the edge.
    startPopLink();
    const lenis = initSmoothScroll();
    return () => lenis?.destroy();
  }, []);

  // Lets CSS respond to the manual motion toggle, not just the OS preference.
  useEffect(() => {
    document.documentElement.dataset.reducedMotion = String(reducedMotion);
  }, [reducedMotion]);

  const path = usePath();
  const experience = findExperience(path);
  const shared = shareNumber(path);

  let body;
  if (shared !== null) {
    body = <SharePage popNumber={shared} />;
  } else if (experience) {
    body = (
      <ErrorBoundary label={experience.title}>
        <Suspense fallback={<div className="route-loading">Loading…</div>}>
          <experience.Component />
        </Suspense>
      </ErrorBoundary>
    );
  } else if (path === "/" || path === "") {
    body = <Home />;
  } else {
    // Any other path is a hall that hasn't been built — say so, in-world,
    // instead of quietly pretending it was the front door.
    body = <MissingHall />;
  }

  return (
    <>
      <Nav />
      {body}
      <Footer />
      <Celebration />
      <GeoSparkles />
    </>
  );
}
